import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Grid,
  Autocomplete
} from '@mui/material';
import { WalteroButton, WalteroTypography } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';
import { fetchDevices } from '../../services/apiService';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import axios from 'axios';

const AssignSensorModal = ({ open, onClose, onSuccess, defaultOrganizationId = '', defaultOrganization = null }) => {
  const [formData, setFormData] = useState({
    sensorId: '',
    organizationId: '',
    subscriptionTypeId: ''
  });
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [availableSubscriptions, setAvailableSubscriptions] = useState([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      loadInitialData();
    }
  }, [open]);

  useEffect(() => {
    if (open && defaultOrganizationId && !formData.organizationId) {
      setFormData(prev => ({
        ...prev,
        organizationId: String(defaultOrganizationId)
      }));
    }
  }, [open, defaultOrganizationId]);

  useEffect(() => {
    if (defaultOrganization && defaultOrganization.id) {
      setOrganizationOptions(prev => {
        const exists = prev.some(option => option.id === defaultOrganization.id);
        if (exists) {
          return prev;
        }
        return [defaultOrganization, ...prev];
      });
    }
  }, [defaultOrganization]);

  useEffect(() => {
    if (formData.organizationId) {
      loadOrganizationSubscriptions();
      loadOrganizationSensors();
    }
  }, [formData.organizationId]);

  const loadInitialData = async () => {
    try {
      setDataLoading(true);
      setError(null);
      // No need to load organizations initially, they'll be loaded on search
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load initial data');
    } finally {
      setDataLoading(false);
    }
  };

  const searchOrganizations = async (searchTerm) => {
    try {
      console.log('🔍 [AssignSensor] Searching organizations with term:', searchTerm);
      const token = localStorage.getItem('authToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let response;
      if (!searchTerm || searchTerm.length === 0) {
        // Load all organizations when no search term
        console.log('📋 [AssignSensor] Loading all organizations');
        response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.LIST}?limit=1000`, { headers });
      } else {
        // Use the search endpoint for filtered results
        console.log('🔍 [AssignSensor] Searching organizations with backend search');
        response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.SEARCH}?q=${encodeURIComponent(searchTerm)}`, { headers });
      }
      
      console.log('📋 [AssignSensor] Organization search response:', response.data);
      console.log('📋 [AssignSensor] Response status:', response.status);
      console.log('📋 [AssignSensor] Response headers:', response.headers);
      
      let organizations = response.data.data || response.data || [];
      console.log('📋 [AssignSensor] Setting organization options:', organizations);
      console.log('📋 [AssignSensor] Number of organizations found:', organizations.length);

      if (defaultOrganization && defaultOrganization.id) {
        const exists = organizations.some(org => org.id === defaultOrganization.id);
        if (!exists) {
          organizations = [defaultOrganization, ...organizations];
        }
      }

      setOrganizationOptions(organizations);
    } catch (error) {
      console.error('❌ [AssignSensor] Error searching organizations:', error);
      console.error('❌ [AssignSensor] Error response:', error.response?.data);
      console.error('❌ [AssignSensor] Error status:', error.response?.status);
      setError(`Failed to search organizations: ${error.response?.data?.message || error.message}`);
    }
  };

  const loadOrganizationSubscriptions = async () => {
    try {
      console.log('📋 Loading subscriptions for organization:', formData.organizationId);
      
      // Load both organization subscriptions and all subscription types
      const [orgSubsResult, typesResult] = await Promise.all([
        subscriptionService.getOrganizationSubscriptions(formData.organizationId),
        subscriptionService.getSubscriptionTypes()
      ]);
      
      console.log('📋 Organization subscriptions response:', orgSubsResult);
      console.log('📋 Subscription types response:', typesResult);
      
      setAvailableSubscriptions(orgSubsResult.data || []);
      setSubscriptionTypes(typesResult.data || []);
    } catch (err) {
      console.error('Error loading organization subscriptions:', err);
      setError('Failed to load organization subscriptions');
    }
  };

  const loadOrganizationSensors = async () => {
    try {
      // Get sensors for this organization from devices API
      const devicesResult = await fetchDevices(formData.organizationId);
      setSensors(devicesResult.data || []);
    } catch (err) {
      console.error('Error loading organization sensors:', err);
      setError('Failed to load organization sensors');
    }
  };

  const handleChange = (field) => (event, childValue) => {
    const rawValue = event?.target?.value !== undefined
      ? event.target.value
      : childValue !== undefined
        ? childValue
        : '';

    const stringValue = typeof rawValue === 'string' ? rawValue : String(rawValue || '');

    if (field === 'organizationId') {
      setFormData(prev => ({
        ...prev,
        organizationId: stringValue,
        sensorId: '',
        subscriptionTypeId: ''
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: stringValue
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.sensorId) {
        throw new Error('Sensor is required');
      }
      if (!formData.organizationId) {
        throw new Error('Organization is required');
      }
      if (!formData.subscriptionTypeId) {
        throw new Error('Subscription type is required');
      }

      // Clean form data to avoid circular references
      const cleanFormData = {
        sensorId: String(formData.sensorId),
        organizationId: String(formData.organizationId),
        subscriptionTypeId: String(formData.subscriptionTypeId)
      };

      console.log('🔍 [AssignSensor] Submitting clean form data:', cleanFormData);

      // Assign sensor subscription
      await subscriptionService.assignSensorSubscription(
        cleanFormData.sensorId,
        cleanFormData.organizationId,
        cleanFormData.subscriptionTypeId
      );
      
      // Reset form and close modal
      resetForm();
      onSuccess?.();
      onClose();
      
    } catch (err) {
      console.error('Error assigning sensor subscription:', err);
      setError(err.message || 'Failed to assign sensor subscription');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      sensorId: '',
      organizationId: '',
      subscriptionTypeId: ''
    });
    setOrganizationOptions([]);
    setAvailableSubscriptions([]);
    setSubscriptionTypes([]);
    setSensors([]);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      setError(null);
      onClose();
    }
  };

  const getSubscriptionDisplayName = (subscription) => {
    console.log('🏷️ Getting display name for subscription:', subscription);
    
    // Find the matching subscription type
    const subscriptionType = subscriptionTypes.find(type => type.id === subscription.subscriptionTypeId);
    console.log('🏷️ Found subscription type:', subscriptionType);
    
    // Use custom name if available, otherwise subscription type name
    const baseName = subscription.customName || subscriptionType?.name || 'Unknown';
    
    // Use override duration if available, otherwise subscription type duration
    const duration = subscription.overrideDurationDays || subscriptionType?.durationDays || 0;
    
    return `${baseName} (${duration} days)`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <WalteroTypography 
          variant="h5" 
          sx={{ 
            color: 'waltero.primary.coreBlue',
            fontWeight: 600 
          }}
        >
          Assign Sensor Subscription
        </WalteroTypography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {dataLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <WalteroTypography>Loading...</WalteroTypography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Organization Selection */}
              <Grid item xs={12}>
                <Autocomplete
                  sx={{ width: '100%' }}
                  value={organizationOptions.find(org => org.id === formData.organizationId) || null}
                  onChange={(event, newValue) => {
                    const orgId = newValue ? String(newValue.id) : '';
                    setFormData(prev => ({
                      ...prev,
                      organizationId: orgId,
                      sensorId: '', // Reset sensor selection when org changes
                      subscriptionTypeId: '' // Reset subscription selection when org changes
                    }));
                  }}
                  onOpen={() => {
                    // Load all organizations when dropdown opens
                    searchOrganizations('');
                  }}
                  onInputChange={(event, newInputValue) => {
                    console.log('🔍 [AssignSensor] Input changed:', newInputValue, 'Length:', newInputValue.length);
                    if (newInputValue.length >= 2) {
                      console.log('🔍 [AssignSensor] Triggering search for:', newInputValue);
                      searchOrganizations(newInputValue);
                    } else if (newInputValue.length === 0) {
                      console.log('🔍 [AssignSensor] Loading all organizations (empty search)');
                      // Load all organizations when search is cleared
                      searchOrganizations('');
                    } else {
                      console.log('🔍 [AssignSensor] Clearing options (search too short)');
                      if (defaultOrganization && defaultOrganization.id) {
                        setOrganizationOptions([defaultOrganization]);
                      } else {
                        setOrganizationOptions([]);
                      }
                    }
                  }}
                  options={organizationOptions}
                  getOptionLabel={(option) => option.name || option.id}
                  disabled={loading}
                  loading={dataLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organization *"
                      placeholder="Search for organization..."
                      helperText="Type at least 2 characters to search. Select organization first to see available sensors and subscriptions"
                    />
                  )}
                />
              </Grid>

              {/* Sensor Selection */}
              {formData.organizationId && (
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    sx={{ width: '100%' }}
                    value={sensors.find(sensor => sensor.id === formData.sensorId) || null}
                    onChange={(event, value) => {
                      const sensorId = value ? String(value.id) : '';
                      setFormData(prev => ({
                        ...prev,
                        sensorId: sensorId
                      }));
                    }}
                    options={sensors}
                    getOptionLabel={(option) => {
                      return `${option.name || option.serialnumber || option.id}${option.serialnumber ? ` (${option.serialnumber})` : ''}`;
                    }}
                    disabled={loading || sensors.length === 0}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sensor *"
                        placeholder={sensors.length === 0 ? "No sensors available" : "Search for sensor..."}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Subscription Type Selection */}
              {formData.organizationId && (
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    disabled={loading || availableSubscriptions.length === 0}
                    sx={{ minWidth: 280, width: '100%' }}
                  >
                    <InputLabel>Subscription Type *</InputLabel>
                    <Select
                      value={formData.subscriptionTypeId || ''}
                      onChange={handleChange('subscriptionTypeId')}
                      label="Subscription Type *"
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            minWidth: 320
                          }
                        }
                      }}
                    >
                      {availableSubscriptions
                        .filter(sub => sub.status === 'active' && sub.subscriptionTypeId)
                        .map((subscription) => (
                        <MenuItem key={subscription.subscriptionTypeId} value={subscription.subscriptionTypeId}>
                          {getSubscriptionDisplayName(subscription)}
                        </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  {availableSubscriptions.length === 0 && formData.organizationId && (
                    <WalteroTypography variant="caption" sx={{ color: 'warning.main', mt: 1, display: 'block' }}>
                      No active subscriptions found for this organization. Please assign a subscription to the organization first.
                    </WalteroTypography>
                  )}
                </Grid>
              )}

              {/* Info box */}
              {formData.organizationId && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, backgroundColor: 'info.light', borderRadius: 1, color: 'info.contrastText' }}>
                    <WalteroTypography variant="body2">
                      <strong>Note:</strong> This will manually assign the sensor to a specific subscription, 
                      overriding the organization's default subscription setting.
                    </WalteroTypography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <WalteroButton 
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
          >
            CANCEL
          </WalteroButton>
          <WalteroButton 
            type="submit"
            disabled={loading || dataLoading || !formData.organizationId || availableSubscriptions.length === 0}
            variant="contained"
          >
            {loading ? 'ASSIGNING...' : 'ASSIGN SENSOR'}
          </WalteroButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AssignSensorModal;
